import React, { useEffect } from 'react';
import classNames from 'classnames';
import { useDataModel } from '@thd-nucleus/data-sources';
import { Card } from '@one-thd/sui-atomic-components';
import { useThdCustomer } from '@thd-olt-functional/customer-information';
import PropTypes from 'prop-types';
import ProjectsContainer from './ProjectsContainer';
import ProjectsHeader from './ProjectsHeader';
import { defaultCardContentProps, MOBILE_BREAKPOINT } from '../constants/responsiveClassNames';
import { projectsDataModel } from './dataModel';
import { ProjectsLoading } from './ProjectsLoading';
import ProjectsError from './ProjectsError';
import ProjectsNullView from './ProjectsNullView';
import { hasExceededMaxRetries, resetRetryTracking } from '../utils/retryTracker';
import { useContainerWidth } from '../hooks/useContainerWidth';

const ProjectsOverview = ({ componentId }) => {
  const [containerRef, width] = useContainerWidth();
  useEffect(() => { LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('projects-overview.ready'); }, []);

  const { svocID } = useThdCustomer() || {};

  const { data, loading, error, refetch } = useDataModel('projects', {
    variables: {
      accountId: svocID,
      skipImages: false,
      skipNumberOfItems: false
    },
    context: { withAuth: true }
  });

  const projects = data?.projects?.projects || [];
  const totalProjectsCount = data?.projects?.totalProjects || 0;
  const isNullView = !projects || projects.length === 0;

  const isCompactView = width < MOBILE_BREAKPOINT;

  // Reset retry tracking on successful data load (even if empty)
  useEffect(() => {
    if (!loading && !error) {
      resetRetryTracking();
    }
  }, [loading, error]);

  let content;

  // Loading skeleton
  if (loading) {
    content = <ProjectsLoading />;
  } else if (error) {
    // Error state - check if max retries exceeded
    // If user has exceeded max retries, show null state as failsafe fallback
    if (hasExceededMaxRetries()) {
      // eslint-disable-next-line no-console
      console.warn('Max retry attempts exceeded. Showing null state as fallback.');
      content = <ProjectsNullView componentId={componentId} />;
    } else {
      content = <ProjectsError refresh={refetch} componentId={componentId} />;
    }
  } else if (isNullView) {
    // Null view for successful load with no projects
    content = <ProjectsNullView componentId={componentId} />;
  } else {
    let headerActions = [];
    if (width > 0) {
      headerActions = isCompactView ? ['createNew'] : ['createNew', 'viewAll'];
    }
    content = (
      <Card
        data-component="ProjectsOverview"
        data-testid="projects-overview"
        className="sui-h-full"
        CardContentProps={{ className: classNames(defaultCardContentProps, 'sui-w-full sui-h-full') }}
      >
        <ProjectsHeader
          projectCount={totalProjectsCount}
          actions={headerActions}
          componentId={componentId}
        />
        <ProjectsContainer
          projects={projects}
          maxMobileProjectSize={3}
          totalProjectsCount={totalProjectsCount}
          iconSize="small"
        />
      </Card>
    );
  }

  return (
    <div ref={containerRef} className="sui-@container sui-h-full">
      {content}
    </div>
  );
};

ProjectsOverview.propTypes = {
  componentId: PropTypes.string,
};

ProjectsOverview.defaultProps = {
  componentId: '',
};

ProjectsOverview.displayName = 'ProjectsOverview';

ProjectsOverview.dataModel = projectsDataModel;

export { ProjectsOverview };
